import { useMemo, useState } from "react";

import organizationTypes from "astrid-firestore/src/api/organizations/constants/organizationTypes";
import productionStatuses from "astrid-firestore/src/api/productions/constants/productionStatuses";
import useCollectionData from "astrid-firestore/src/hooks/useCollectionData";
import useLoadingValues from "astrid-firestore/src/hooks/useLoadingValues";
import uniqueArray from "astrid-helpers/src/uniqueArray";

import useCostsQuery from "../../../../costs/hooks/useCostsQuery";
import useOpenCosts from "../../../../costs/hooks/useOpenCosts";
import useExchangeRate from "../../../../exchangeRates/hooks/useExchangeRate";

const partyTypes = [organizationTypes.PRODUCER, organizationTypes.PUBLISHER];

export default function useProductionFinance({ partyId, production, defaultCurrency = "EUR" }) {
	const [currency, setCurrency] = useState(production?.productionPrice?.currency || defaultCurrency);

	const costsQuery = useCostsQuery({ productionId: production?.id });

	const firstDoneDate = production?.statusTimeline
		?.find(({ status }) => status === productionStatuses.DONE)
		?.timestamp.toDate();

	const [[exchangeRate, prices = [], users = [], costs = []], loading, error] = useLoadingValues(
		useExchangeRate(firstDoneDate),
		useCollectionData(production?.ref?.collection("productionPrices").where("deleted", "==", false)),
		useCollectionData(production?.ref?.collection("users")),
		useCollectionData(production?.id && costsQuery),
	);

	const openCosts = useOpenCosts({ users, production, exchangeRate, costs, prices });

	const groupedCosts = useMemo(() => groupCostsByPartyId(costs, openCosts), [costs, openCosts]);

	const finalizableOpenCosts = partyId === production.publisher ? groupedCosts[partyId]?.openCosts : openCosts;

	const rate = exchangeRate?.rates[currency] || 1;

	const totalEstimatedOpenAmount = (groupedCosts[partyId]?.openCosts || []).reduce(
		(acc, { estimation, price, expense, secondParty }) => {
			return (
				acc +
				(price?.currency === currency ? estimation.value : (estimation.exchanged || 0) * rate) *
					(expense ? -1 : 1) *
					(partyId === secondParty?.id ? -1 : 1)
			);
		},
		0,
	);

	const totalOpenAmount = (groupedCosts[partyId]?.openCosts || []).reduce(
		(acc, { total, price, expense, exchange, secondParty }) => {
			return (
				acc +
				(price?.currency === currency ? total : (exchange?.value || 0) * rate) *
					(expense ? -1 : 1) *
					(partyId === secondParty?.id ? -1 : 1)
			);
		},
		0,
	);

	const totalFinalizedAmount = (groupedCosts[partyId]?.costs || []).reduce(
		(acc, { total, price, expense, exchange, secondParty }) => {
			return (
				acc +
				(price?.currency === currency ? total : (exchange?.value || 0) * rate) *
					(expense ? -1 : 1) *
					(partyId === secondParty?.id ? -1 : 1)
			);
		},
		0,
	);

	const totalExpenseAmount = (groupedCosts[partyId]?.costs || [])
		.concat(groupedCosts[partyId]?.openCosts || [])
		.filter(({ expense, exchange }) => expense && exchange)
		.map(({ total, price, exchange }) => (price?.currency === currency ? total : (exchange?.value || 0) * rate))
		.reduce((acc, value) => acc + value, 0);

	const totalRevenueAmount = (groupedCosts[partyId]?.costs || [])
		.concat(groupedCosts[partyId]?.openCosts || [])
		.filter(({ expense, exchange }) => !expense && exchange)
		.map(({ total, price, exchange }) => (price?.currency === currency ? total : (exchange?.value || 0) * rate))
		.reduce((acc, value) => acc + value, 0);

	const menuOptions = Object.values(groupedCosts).map(({ party, openCosts }) => ({
		key: party.id,
		name: party.name,
		path: `/production/${production.id}/finance/${party.id}`,
		label: openCosts?.filter(({ openAmount }) => openAmount > 0).length || 0,
		active: party.id === partyId,
	}));

	if (!menuOptions.find(({ key }) => key === production.producer)) {
		menuOptions.unshift({
			key: production.producer,
			name: production.producerName,
			path: `/production/${production.id}/finance/${production.producer}`,
			active: production.producer === partyId,
		});
	}

	const productionCurrencies = uniqueArray(
		[production?.productionPrice?.currency, ...openCosts.map(({ price }) => price?.currency)].filter(Boolean),
	);

	const currencyOptions = productionCurrencies.map((currency) => ({
		key: currency,
		value: currency,
		text: currency,
	}));

	return [
		{
			costs: groupedCosts[partyId]?.costs || [],
			openCosts: groupedCosts[partyId]?.openCosts || [],
			currency,
			setCurrency,
			currencyOptions,
			menuOptions,
			finalizableOpenCosts,
			totalOpenAmount,
			totalExpenseAmount,
			totalRevenueAmount,
			totalFinalizedAmount,
			totalEstimatedOpenAmount,
		},
		loading,
		error,
	];
}

function groupCostsByPartyId(costs, openCosts) {
	const groupedCosts = {};

	costs.forEach((cost) => {
		addCostToGroup(groupedCosts, "costs", cost, cost.firstParty);
		addCostToGroup(groupedCosts, "costs", cost, cost.secondParty);
	});

	openCosts.forEach((cost) => {
		addCostToGroup(groupedCosts, "openCosts", cost, cost.firstParty);
		addCostToGroup(groupedCosts, "openCosts", cost, cost.secondParty);
	});

	return groupedCosts;
}

function addCostToGroup(groupedCosts, name, cost, party) {
	if (partyTypes.includes(party?.type)) {
		groupedCosts[party.id] = groupedCosts[party.id] || { party };
		groupedCosts[party.id][name] = groupedCosts[party.id][name] || [];
		groupedCosts[party.id][name].push(cost);
	}
}
